import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../lib/store.ts';
import { showPaywall } from '../../lib/plan.ts';
import { SETUP_KEY, type SetupStatus } from '../../hooks/useSetup.ts';
import { DEFAULT_ZIP_MAX_MB, UploadRidesDialog, type UploadSection } from '../UploadRidesDialog.tsx';
import { LucideIcon, type LucideName } from '../icons/LucideIcon.tsx';

type Option = 'files' | 'zip' | 'link';

const ROWS: readonly { id: Option; icon: LucideName; pro: boolean }[] = [
  { id: 'files', icon: 'file-up', pro: false },
  { id: 'zip', icon: 'file-archive', pro: true },
  { id: 'link', icon: 'link', pro: true },
];

/**
 * "Bring your older rides" (docs/ONBOARDING_PLAN.md): ride files (free, the
 * monthly allowance), a history ZIP up to ``zip_max_mb`` and a data-export
 * download link (both Pro; free accounts get the upgrade entry). Shown in
 * the wizard's Connect step when nothing is connected, and in Your older
 * rides. The source app is never named.
 */
export function HistoryOptions({ history }: Readonly<{ history: SetupStatus['history'] }>) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useStore((s) => s.user);
  const [open, setOpen] = useState<UploadSection | null>(null);
  const pro = history.zip_import === 'available';
  const mb = history.zip_max_mb ?? DEFAULT_ZIP_MAX_MB;
  const monthly = user?.free_uploads_monthly;

  const body = (id: Option) => {
    if (id === 'zip') return t('setup.historyOptions.zipBody', { mb });
    if (id === 'link') return t('setup.historyOptions.linkBody');
    return pro || monthly == null
      ? t('setup.historyOptions.filesPro')
      : t('setup.historyOptions.filesFree', { count: monthly });
  };
  const pick = (id: Option) => {
    if (id === 'files') setOpen('files');
    else if (pro) setOpen('history');
    else showPaywall('history_import');
  };
  const close = () => {
    setOpen(null);
    void qc.invalidateQueries({ queryKey: SETUP_KEY });
  };

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-semibold text-(--color-text-primary)">{t('setup.historyOptions.title')}</h2>
      <p className="text-sm text-(--color-text-secondary)">{t('setup.historyOptions.body')}</p>
      {ROWS.map((row) => (
        <button
          key={row.id}
          type="button"
          onClick={() => pick(row.id)}
          className="w-full text-left rounded-xl bg-(--color-bg-secondary) border border-(--color-border) hover:border-(--color-accent)/50 p-4 flex items-start gap-3"
        >
          <LucideIcon name={row.icon} size={20} className="mt-0.5 text-(--color-text-secondary)" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-(--color-text-primary)">
              {t(`setup.historyOptions.${row.id}`)}
            </span>
            <span className="block text-xs text-(--color-text-secondary) mt-0.5">{body(row.id)}</span>
          </span>
          {row.pro && !pro && (
            <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium border border-(--color-accent)/40 text-(--color-accent)">
              {t('sources.import.pro')}
            </span>
          )}
        </button>
      ))}
      {open && <UploadRidesDialog section={open} onClose={close} />}
    </section>
  );
}
